import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import RadioQuestion from './RadioQuestion';

export default function ConditionalQuestions({ isApartment, isMonument, isDetached, apartmentDetails, setApartmentDetails, monumentDetails, setMonumentDetails, detachedDetails, setDetachedDetails }) {
  const { t } = useTranslation();

  if (!isApartment && !isMonument && !isDetached) return null;

  return (
    <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border/50" data-testid="conditional-questions">
      <p className="text-sm font-medium text-primary mb-4">{t('quiz.additionalQuestions')}</p>

      {isApartment && (
        <div className="space-y-4" data-testid="apartment-questions">
          <RadioQuestion
            label={t('quiz.apartment.floorLevel')}
            value={apartmentDetails.floor_level}
            options={[
              { value: 'ground', label: t('quiz.apartment.ground') },
              { value: 'middle', label: t('quiz.apartment.middle') },
              { value: 'top', label: t('quiz.apartment.top') },
            ]}
            onChange={(val) => setApartmentDetails(prev => ({ ...prev, floor_level: val }))}
            testIdPrefix="apt-floor"
          />
          <RadioQuestion
            label={t('quiz.apartment.sharedBuilding')}
            value={apartmentDetails.shared_building}
            options={[
              { value: 'yes', label: t('quiz.yes') },
              { value: 'no', label: t('quiz.no') },
              { value: 'not_sure', label: t('quiz.notSure') },
            ]}
            onChange={(val) => setApartmentDetails(prev => ({ ...prev, shared_building: val }))}
            testIdPrefix="apt-shared"
          />
          <RadioQuestion
            label={t('quiz.apartment.shellInfluence')}
            value={apartmentDetails.shell_influence}
            options={[
              { value: 'yes', label: t('quiz.apartment.canInfluence') },
              { value: 'no', label: t('quiz.apartment.cannotInfluence') },
              { value: 'partial', label: t('quiz.apartment.partialInfluence') },
            ]}
            onChange={(val) => setApartmentDetails(prev => ({ ...prev, shell_influence: val }))}
            testIdPrefix="apt-shell"
          />
        </div>
      )}

      {isMonument && (
        <div className="space-y-4" data-testid="monument-questions">
          <RadioQuestion
            label={t('quiz.monument.protectedStatus')}
            value={monumentDetails.protected_status}
            options={[
              { value: 'yes', label: t('quiz.yes') },
              { value: 'no', label: t('quiz.no') },
              { value: 'unknown', label: t('quiz.notSure') },
            ]}
            onChange={(val) => setMonumentDetails(prev => ({ ...prev, protected_status: val }))}
            testIdPrefix="mon-status"
          />
          <div className="space-y-3">
            <Label className="block text-sm">{t('quiz.monument.restrictions')}</Label>
            {[
              { key: 'facade_restrictions', label: t('quiz.monument.facade') },
              { key: 'roof_restrictions', label: t('quiz.monument.roof') },
              { key: 'window_restrictions', label: t('quiz.monument.windows') },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2 p-2">
                <Checkbox
                  id={key}
                  checked={monumentDetails[key]}
                  onCheckedChange={(checked) => setMonumentDetails(prev => ({ ...prev, [key]: checked }))}
                />
                <Label htmlFor={key} className="cursor-pointer text-sm">{label}</Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {isDetached && (
        <div className="space-y-4" data-testid="detached-questions">
          <RadioQuestion
            label={t('quiz.detached.technicalSpace')}
            value={detachedDetails.technical_space}
            options={[
              { value: 'available', label: t('quiz.detached.available') },
              { value: 'limited', label: t('quiz.detached.limited') },
              { value: 'none', label: t('quiz.insulation.none') },
            ]}
            onChange={(val) => setDetachedDetails(prev => ({ ...prev, technical_space: val }))}
            testIdPrefix="det-space"
          />
          <div className="flex items-center space-x-2 p-3 border rounded-md">
            <Checkbox
              id="large-heat"
              checked={detachedDetails.large_heat_demand}
              onCheckedChange={(checked) => setDetachedDetails(prev => ({ ...prev, large_heat_demand: checked }))}
            />
            <Label htmlFor="large-heat" className="cursor-pointer text-sm">{t('quiz.detached.largeHeatDemand')}</Label>
          </div>
        </div>
      )}
    </div>
  );
}
